import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { WO2Checkbox, WO2ToggleSwitch } from 'src/common';
import { BaseReportComponentProps, HidingOptionHoldingsVsTargetVariance, HidingOptionsRequest, MdaReportTypes } from '../../store/types';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { DateRange } from 'src/store';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { ClientPortfolio } from '../../../common/store';

export interface HoldingsVsTargetVarianceProps extends BaseReportComponentProps {
  setHoldingsVsTargetVarianceSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setAsAtDateHoldingsVsTargetVarianceParameter: ActionCreatorWithPayload<DateRange>;
  setHoldingsVsTargetPortfolioParameter?: ActionCreatorWithPayload<number>;
  setHoldingsVsTargetVarianceHidingOptions: ActionCreatorWithPayload<HidingOptionsRequest<HidingOptionHoldingsVsTargetVariance>, string>;
  expandHoldingsVsTargetVariance: ActionCreatorWithoutPayload<string>;
  clientPortfolios: ClientPortfolio[];
}

export const HoldingsVsTargetVariance = ({
  parameters,
  setHoldingsVsTargetVarianceSelectedParameter,
  setAsAtDateHoldingsVsTargetVarianceParameter,
  setHoldingsVsTargetVarianceHidingOptions,
  setHoldingsVsTargetPortfolioParameter,
  createReportSortRequest,
  expandHoldingsVsTargetVariance,
  clientPortfolios,
}: HoldingsVsTargetVarianceProps) => {
  const handleClickHoldingsVsTargetVarianceSelect = () => {
    setHoldingsVsTargetVarianceSelectedParameter(!parameters.selectedReports?.holdingsVsTargetVariance.selected);
    createReportSortRequest(
      !parameters.selectedReports?.holdingsVsTargetVariance.selected,
      MdaReportTypes.HoldingsVsTargetVariance.id,
      MdaReportTypes.HoldingsVsTargetVariance.displayName
    );
  };

  const handleSelectPortfolio = (portfolio: ClientPortfolio | null) => {
    if (!setHoldingsVsTargetPortfolioParameter) return;
    setHoldingsVsTargetPortfolioParameter(portfolio?.investmentProgramId ?? -1);
  };

  const handleClickDateForHoldingsVsTargetVariance = (dates: DateRange) => {
    setAsAtDateHoldingsVsTargetVarianceParameter(dates);
  };

  return (
    <Accordion
      onChange={expandHoldingsVsTargetVariance}
      expanded={parameters.selectedReports?.holdingsVsTargetVariance.expanded || false}
      elevation={0}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.holdingsVsTargetVariance.selected}
              onChangeHandler={handleClickHoldingsVsTargetVarianceSelect}
            />
          }
          label="Holdings vs Target Variance"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            sx={{ minWidth: 194 }}
            dateRange={{
              dateFrom: '',
              dateTo: parameters?.selectedReports.holdingsVsTargetVariance?.asAtDate,
              dateRangeType: parameters?.selectedReports.holdingsVsTargetVariance?.asAtDateType,
            }}
            dateOnlyLabel="As at"
            setDateRange={handleClickDateForHoldingsVsTargetVariance}
            inceptionDate={parameters.inceptionDate}
            isDateOnly
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" spacing={1}>
          {setHoldingsVsTargetPortfolioParameter && (
            <Grid item>
              <Autocomplete
                noOptionsText="No items to select"
                id="holdingsvstarget-multiselect"
                fullWidth
                limitTags={1}
                value={
                  clientPortfolios.find(
                    (portfolio) => portfolio.investmentProgramId === parameters.selectedReports.holdingsVsTargetVariance.portfolioId
                  ) || null
                }
                options={clientPortfolios}
                onChange={(_e: React.SyntheticEvent<Element, Event>, value: ClientPortfolio | null) => handleSelectPortfolio(value)}
                getOptionLabel={(option: ClientPortfolio) => option.investmentServiceName}
                renderInput={(params) => <TextField {...params} label="Select a Portfolio" margin="normal" fullWidth />}
                isOptionEqualToValue={(option: ClientPortfolio, value: ClientPortfolio) => option.investmentProgramId === value.investmentProgramId}
              />
            </Grid>
          )}
          <Grid item container spacing={2} alignItems="center">
            <Grid item container xs={6} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setHoldingsVsTargetVarianceHidingOptions({
                    value: e.target.checked,
                    option: HidingOptionHoldingsVsTargetVariance.ExclusionList,
                  })
                }
                checked={parameters.selectedReports.holdingsVsTargetVariance.applyExclusionList}
                color="primary"
              />
              <Typography variant="h4">Apply Exclusion List</Typography>
            </Grid>
            <Grid item container xs={6} alignItems="center">
              <WO2ToggleSwitch
                onChange={(e) =>
                  setHoldingsVsTargetVarianceHidingOptions({
                    value: e.target.checked,
                    option: HidingOptionHoldingsVsTargetVariance.Summary,
                  })
                }
                checked={parameters.selectedReports.holdingsVsTargetVariance.includeSummary}
                color="primary"
              />
              <Typography variant="h4">Include Summary</Typography>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
